/**
 * Shared regional eSIM catalogue helpers for Home + Shop (from home-data snapshot).
 */

import type { HomeDestinationDto, HomeRegionalCatalogDto, HomeRegionalRegionDto } from '@/modules/plans/services/homeData.service'
import {
  transformApiBundle,
  type ApiBundle,
  type Bundle,
} from '@/modules/plans/services/plans.service'
import { getCountryNameFromIso } from '@/modules/countries/utils/countryIsoMap'
import { bundlePricePerGb } from '@/modules/plans/utils/shopRegionCatalog'
import { getCountryFlag } from '@/shared/utils/countryFlags'
import type { eSIMPlan } from '@/shared/types'

export type RegionalCountryRow = {
  name: string
  iso: string
  flag: string
  priceStr: string
  available: boolean
}

export type RegionalBundleRow = {
  bundle: Bundle
  title: string
  pricePerGb: string
  available: boolean
  coverageCountryCount: number
}

export type RegionalCountryCountVariant = {
  countryCount: number
  bundles: RegionalBundleRow[]
  countries: RegionalCountryRow[]
}

export function slugifyRegionLabel(label: string): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'region'
}

export function getRegionalCatalogRegions(
  catalog: HomeRegionalCatalogDto | undefined | null
): HomeRegionalRegionDto[] {
  return catalog?.regions ?? []
}

export function findRegionalRegion(
  catalog: HomeRegionalCatalogDto | undefined | null,
  key: string | null | undefined
): HomeRegionalRegionDto | null {
  if (!catalog || !key) return null
  return catalog.regions.find(r => r.key === key) ?? null
}

export function filterRegionalRegions(
  catalog: HomeRegionalCatalogDto | undefined | null,
  searchQuery: string
): HomeRegionalRegionDto[] {
  const regions = getRegionalCatalogRegions(catalog)
  const q = searchQuery.trim().toLowerCase()
  if (!q) return regions
  return regions.filter(region => {
    if (region.label.toLowerCase().includes(q)) return true
    return (region.countries ?? []).some(c => c.name.toLowerCase().includes(q))
  })
}

export function mapRegionalCountries(region: HomeRegionalRegionDto | null): RegionalCountryRow[] {
  if (!region) return []
  return (region.countries ?? []).map(d => ({
    name: d.name,
    iso: d.iso,
    flag: getCountryFlag(d.iso),
    priceStr:
      d.available && d.startingFromUsd != null ? d.startingFromUsd.toFixed(2) : '0',
    available: d.available,
  }))
}

function isDisplayableRegionalBundle(bundle: Bundle): boolean {
  return !bundle.unlimited && (bundle.dataAmount ?? 0) > 0
}

function mapApiBundleToRegionalRow(
  api: ApiBundle,
  regionLabel: string
): RegionalBundleRow | null {
  const bundle = transformApiBundle(api)
  if (!isDisplayableRegionalBundle(bundle)) return null
  const ppg = bundlePricePerGb(bundle)
  const title = (bundle.description || bundle.name || regionLabel).slice(0, 56)
  return {
    bundle,
    title,
    pricePerGb: ppg != null ? ppg.toFixed(2) : '0',
    available: ppg != null,
    coverageCountryCount: extractApiBundleCoverage(api).length,
  }
}

export function mapRegionalBundles(region: HomeRegionalRegionDto | null): RegionalBundleRow[] {
  if (!region) return []
  return (region.bundles ?? [])
    .map(api => mapApiBundleToRegionalRow(api, region.label))
    .filter((row): row is RegionalBundleRow => row != null)
}

function looksLikeIsoAlpha2(value: string): boolean {
  return /^[A-Za-z]{2}$/.test(value)
}

function resolveRegionalCountryDisplayName(
  name: string,
  iso: string,
  regionEntry?: HomeDestinationDto
): string {
  const rawName = name.trim()
  const isoUpper = iso.trim().toUpperCase()

  if (regionEntry?.name && !looksLikeIsoAlpha2(regionEntry.name)) {
    return regionEntry.name.trim()
  }
  if (rawName && !looksLikeIsoAlpha2(rawName)) {
    return rawName
  }
  if (rawName && looksLikeIsoAlpha2(rawName)) {
    const fromName = getCountryNameFromIso(rawName)
    if (fromName) return fromName
  }
  if (isoUpper && looksLikeIsoAlpha2(isoUpper)) {
    const fromIso = getCountryNameFromIso(isoUpper)
    if (fromIso) return fromIso
  }
  return rawName || isoUpper
}

function mapCoverageToCountryRows(
  coverage: HomeDestinationDto[],
  regionCountries: HomeDestinationDto[]
): RegionalCountryRow[] {
  const priceByIso = new Map(
    regionCountries.map(c => [c.iso.trim().toUpperCase(), c])
  )
  return coverage.map(c => {
    const regionEntry = priceByIso.get(c.iso.trim().toUpperCase())
    return {
      name: resolveRegionalCountryDisplayName(c.name, c.iso, regionEntry),
      iso: c.iso,
      flag: getCountryFlag(c.iso),
      priceStr:
        regionEntry?.available && regionEntry.startingFromUsd != null
          ? regionEntry.startingFromUsd.toFixed(2)
          : '0',
      available: regionEntry?.available ?? true,
    }
  })
}

function unionCoverageFromBundles(apis: ApiBundle[]): HomeDestinationDto[] {
  const seen = new Set<string>()
  const out: HomeDestinationDto[] = []
  for (const api of apis) {
    for (const country of extractApiBundleCoverage(api)) {
      const iso = country.iso.trim().toUpperCase()
      if (seen.has(iso)) continue
      seen.add(iso)
      out.push(country)
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name))
}

/** Groups displayable regional bundles by how many countries each plan covers. */
export function buildRegionalCountryCountVariants(
  region: HomeRegionalRegionDto | null
): RegionalCountryCountVariant[] {
  if (!region) return []

  const byCount = new Map<number, ApiBundle[]>()
  for (const api of region.bundles ?? []) {
    const row = mapApiBundleToRegionalRow(api, region.label)
    if (!row) continue
    const existing = byCount.get(row.coverageCountryCount) ?? []
    existing.push(api)
    byCount.set(row.coverageCountryCount, existing)
  }

  return Array.from(byCount.entries())
    .sort(([a], [b]) => a - b)
    .map(([countryCount, apis]) => ({
      countryCount,
      bundles: apis
        .map(api => mapApiBundleToRegionalRow(api, region.label))
        .filter((row): row is RegionalBundleRow => row != null),
      countries: mapCoverageToCountryRows(
        unionCoverageFromBundles(apis),
        region.countries ?? []
      ),
    }))
}

export function buildRegionalCheckoutPlan(bundle: Bundle, regionLabel: string): eSIMPlan {
  return {
    id: bundle.id,
    name: bundle.description || bundle.name || `${regionLabel} - ${bundle.data}`,
    description: bundle.description || `${bundle.data} regional eSIM for ${regionLabel}`,
    price: bundle.price,
    currency: bundle.currency || 'USD',
    data: bundle.data,
    validity: bundle.validity || '30 days',
    regions: [regionLabel],
    features: [],
    popular: false,
    bundleId: bundle.id,
  }
}

/** Coverage countries from a raw API bundle (mirrors backend + transformBundle rules). */
export function extractApiBundleCoverage(api: ApiBundle): HomeDestinationDto[] {
  const raw = api.countries ?? []
  const roaming = api.roamingCountries ?? []
  const c0 = raw[0]
  const isGlobalMarker = [c0?.name, c0?.iso, c0?.region].some(
    v => String(v ?? '').toLowerCase() === 'global'
  )

  let coverage = raw
  if (isGlobalMarker && roaming.length > 0) {
    coverage = roaming
  } else if (c0 && isRegionalMarker(c0)) {
    coverage = roaming.length > 0 ? roaming : raw.length > 1 ? raw.slice(1) : []
  } else if (roaming.length > 0) {
    coverage = roaming
  }

  const isNomad = (api.name ?? '').toLowerCase().startsWith('nomad:')
  if (isNomad && raw.length > 1 && coverage === raw) {
    coverage = raw
  }

  const seen = new Set<string>()
  const out: HomeDestinationDto[] = []
  for (const c of coverage) {
    if (!c?.iso || !c?.name) continue
    if (isRegionalMarker(c)) continue
    const iso = c.iso.trim().toUpperCase()
    if (seen.has(iso)) continue
    seen.add(iso)
    out.push({ name: c.name.trim(), iso, startingFromUsd: null, available: true })
  }
  return out
}

function isRegionalMarker(c: { iso?: string; region?: string }): boolean {
  if (!c.iso || !c.region) return false
  return c.iso.trim() === c.region.trim()
}
